export type BenefitCategoryTab = 'health' | 'supplemental' | 'retirement' | 'pnc' | 'other';

export interface ProductBenefitMapping {
  category: BenefitCategoryTab;
  subcategory?: string;
  requiresApproval?: boolean;
}

export interface PayrollBenefitSubcategoryDefinition {
  id: string;
  label: string;
}

export type PayrollBenefitSubcategoryDefinitionsByCategory =
  Partial<Record<BenefitCategoryTab, PayrollBenefitSubcategoryDefinition[]>>;

export interface PayrollBenefitSectionDefinition {
  id: string;
  title: string;
  subcategory?: string;
}

const CATEGORY_LABELS: Record<BenefitCategoryTab, string> = {
  health: 'Health',
  supplemental: 'Supplemental',
  retirement: 'Retirement',
  pnc: 'P&C',
  other: 'Other',
};

export const PAYROLL_BENEFIT_CATEGORY_OPTIONS: BenefitCategoryTab[] = [
  'health',
  'supplemental',
  'retirement',
  'pnc',
  'other',
];

export const DEFAULT_PAYROLL_BENEFIT_SUBCATEGORY_DEFINITIONS_BY_CATEGORY:
  Record<BenefitCategoryTab, PayrollBenefitSubcategoryDefinition[]> = {
    health: [
      { id: 'medical', label: 'Medical' },
      { id: 'medical-ichra', label: 'Medical - ICHRA' },
      { id: 'dental', label: 'Dental' },
      { id: 'vision', label: 'Vision' },
      { id: 'hsa', label: 'Health Savings Accounts (HSA)' },
      { id: 'fsa', label: 'Flexible Spending Accounts (FSA)' },
    ],
    supplemental: [],
    retirement: [],
    pnc: [
      { id: 'homeowners', label: 'Homeowners Insurance' },
      { id: 'renters', label: 'Renters Insurance' },
    ],
    other: [
      { id: 'pet', label: 'Pet Insurance' },
      { id: 'wellness', label: 'Wellness' },
      { id: 'employee-assistance', label: 'Employee Assistance' },
    ],
  };

export function getBenefitCategoryLabel(category: BenefitCategoryTab): string {
  return CATEGORY_LABELS[category];
}

export function humanizePayrollBenefitSubcategoryId(subcategoryId: string): string {
  const normalizedId = `${subcategoryId || ''}`.trim();
  if (!normalizedId) {
    return '';
  }

  const upperAcronyms = new Set(['hsa', 'fsa', 'hmo', 'ppo', 'ichra', 'hdhp', 'hraa', 'hcr']);
  return normalizedId
    .split('-')
    .filter((segment) => !!segment)
    .map((segment) => {
      const normalizedSegment = segment.trim();
      if (!normalizedSegment) {
        return '';
      }
      if (upperAcronyms.has(normalizedSegment.toLowerCase())) {
        return normalizedSegment.toUpperCase();
      }
      return normalizedSegment.charAt(0).toUpperCase() + normalizedSegment.slice(1).toLowerCase();
    })
    .join(' ');
}

function normalizeBenefitCategory(category: any): BenefitCategoryTab | '' {
  const normalizedCategory = `${category || ''}`.trim() as BenefitCategoryTab;
  return PAYROLL_BENEFIT_CATEGORY_OPTIONS.includes(normalizedCategory) ? normalizedCategory : '';
}

function normalizeBenefitSubcategoryId(subcategoryId: any): string {
  return `${subcategoryId || ''}`.trim().toLowerCase();
}

function canonicalizeCategoryAndSubcategory(
  category: BenefitCategoryTab,
  subcategoryId: string,
): { category: BenefitCategoryTab; subcategoryId: string } {
  if (category === 'pnc' && subcategoryId === 'pet') {
    return {
      category: 'other',
      subcategoryId,
    };
  }

  return {
    category,
    subcategoryId,
  };
}

function normalizeSubcategoryDefinitionEntry(entry: any): PayrollBenefitSubcategoryDefinition | null {
  const normalizedId = normalizeBenefitSubcategoryId(entry?.id);
  if (!normalizedId) {
    return null;
  }

  const normalizedLabel = `${entry?.label || ''}`.trim() || humanizePayrollBenefitSubcategoryId(normalizedId);
  return {
    id: normalizedId,
    label: normalizedLabel,
  };
}

export function normalizePayrollBenefitSubcategoryDefinitionsByCategory(
  input: any,
): PayrollBenefitSubcategoryDefinitionsByCategory {
  const normalized: PayrollBenefitSubcategoryDefinitionsByCategory = {};

  PAYROLL_BENEFIT_CATEGORY_OPTIONS.forEach((category) => {
    if (!Object.prototype.hasOwnProperty.call(input || {}, category)) {
      return;
    }

    const rawEntries = Array.isArray(input?.[category]) ? input[category] : [];
    const seenIds = new Set<string>();
    const nextEntries: PayrollBenefitSubcategoryDefinition[] = [];

    rawEntries.forEach((entry: any) => {
      const normalizedEntry = normalizeSubcategoryDefinitionEntry(entry);
      if (!normalizedEntry) {
        return;
      }

      const canonicalized = canonicalizeCategoryAndSubcategory(category, normalizedEntry.id);
      const targetCategory = canonicalized.category;
      const normalizedId = canonicalized.subcategoryId;
      if (targetCategory !== category || seenIds.has(normalizedId)) {
        return;
      }

      nextEntries.push({
        id: normalizedId,
        label: normalizedEntry.label,
      });
      seenIds.add(normalizedId);
    });

    normalized[category] = nextEntries;
  });

  return normalized;
}

export function getPayrollBenefitSubcategoryDefinitionsByCategory(
  definitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory,
): Record<BenefitCategoryTab, PayrollBenefitSubcategoryDefinition[]> {
  const normalizedDefinitionsByCategory = normalizePayrollBenefitSubcategoryDefinitionsByCategory(
    definitionsByCategory || {}
  );

  return PAYROLL_BENEFIT_CATEGORY_OPTIONS.reduce(
    (result: Record<BenefitCategoryTab, PayrollBenefitSubcategoryDefinition[]>, category) => {
      if (Object.prototype.hasOwnProperty.call(normalizedDefinitionsByCategory, category)) {
        result[category] = [...(normalizedDefinitionsByCategory[category] || [])];
        return result;
      }

      result[category] = [...DEFAULT_PAYROLL_BENEFIT_SUBCATEGORY_DEFINITIONS_BY_CATEGORY[category]];
      return result;
    },
    {} as Record<BenefitCategoryTab, PayrollBenefitSubcategoryDefinition[]>,
  );
}

export function getPayrollBenefitSubcategoryDefinitionsForCategory(
  category: BenefitCategoryTab | '' | undefined,
  definitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory,
): PayrollBenefitSubcategoryDefinition[] {
  const normalizedCategory = normalizeBenefitCategory(category);
  if (!normalizedCategory) {
    return [];
  }

  return getPayrollBenefitSubcategoryDefinitionsByCategory(definitionsByCategory)[normalizedCategory] || [];
}

export function getPayrollBenefitSubcategoryOptions(
  category: BenefitCategoryTab | '' | undefined,
  definitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory,
): string[] {
  return getPayrollBenefitSubcategoryDefinitionsForCategory(category, definitionsByCategory).map(
    (definition) => definition.id
  );
}

export function getPayrollBenefitSubcategoryLabel(
  category: BenefitCategoryTab | '' | undefined,
  subcategoryId: string | undefined,
  definitionsByCategory?: PayrollBenefitSubcategoryDefinitionsByCategory,
): string {
  const normalizedCategory = normalizeBenefitCategory(category);
  const normalizedSubcategoryId = normalizeBenefitSubcategoryId(subcategoryId);
  if (!normalizedCategory || !normalizedSubcategoryId) {
    return '';
  }

  const definitions = getPayrollBenefitSubcategoryDefinitionsForCategory(normalizedCategory, definitionsByCategory);
  const matchedDefinition = definitions.find((definition) => definition.id === normalizedSubcategoryId);
  return matchedDefinition?.label || humanizePayrollBenefitSubcategoryId(normalizedSubcategoryId);
}

export function generatePayrollBenefitSubcategoryId(
  label: string,
  existingIds: string[] = [],
): string {
  const normalizedLabel = `${label || ''}`.trim().toLowerCase();
  const baseId = normalizedLabel
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const fallbackId = baseId || 'subcategory';
  const normalizedExistingIds = new Set(
    (existingIds || [])
      .map((id) => normalizeBenefitSubcategoryId(id))
      .filter((id) => !!id)
  );

  if (!normalizedExistingIds.has(fallbackId)) {
    return fallbackId;
  }

  let suffix = 2;
  while (normalizedExistingIds.has(`${fallbackId}-${suffix}`)) {
    suffix++;
  }
  return `${fallbackId}-${suffix}`;
}

export function buildPayrollBenefitSectionDefinitions(
  category: BenefitCategoryTab,
  definitionsByCategory: PayrollBenefitSubcategoryDefinitionsByCategory | undefined,
  productMappings: ProductBenefitMapping[] = [],
): PayrollBenefitSectionDefinition[] {
  const definitions = getPayrollBenefitSubcategoryDefinitionsForCategory(category, definitionsByCategory);
  const knownDefinitionIds = new Set(definitions.map((definition) => definition.id));
  const unknownSubcategoryIds: string[] = [];

  (productMappings || []).forEach((mapping) => {
    if (mapping?.category !== category) {
      return;
    }

    const normalizedSubcategoryId = normalizeBenefitSubcategoryId(mapping?.subcategory);
    if (!normalizedSubcategoryId || knownDefinitionIds.has(normalizedSubcategoryId)) {
      return;
    }

    knownDefinitionIds.add(normalizedSubcategoryId);
    unknownSubcategoryIds.push(normalizedSubcategoryId);
  });

  return [
    ...definitions.map((definition) => ({
      id: definition.id,
      title: definition.label,
      subcategory: definition.id,
    })),
    ...unknownSubcategoryIds.map((subcategoryId) => ({
      id: subcategoryId,
      title: humanizePayrollBenefitSubcategoryId(subcategoryId),
      subcategory: subcategoryId,
    })),
  ];
}

export function normalizePayrollBenefitMapping(entry: any): ProductBenefitMapping | null {
  if (typeof entry === 'string') {
    const category = normalizeBenefitCategory(entry);
    return category ? { category } : null;
  }

  const rawCategory = normalizeBenefitCategory(entry?.category);
  if (!rawCategory) {
    return null;
  }

  const rawSubcategoryId = normalizeBenefitSubcategoryId(entry?.subcategory);
  const canonicalized = canonicalizeCategoryAndSubcategory(rawCategory, rawSubcategoryId);
  const next: ProductBenefitMapping = { category: canonicalized.category };

  if (canonicalized.subcategoryId) {
    next.subcategory = canonicalized.subcategoryId;
  }

  if (entry?.requiresApproval === true) {
    next.requiresApproval = true;
  }

  return next;
}

export function normalizeProductPayrollBenefitMap(map: Record<string, any>): Record<string, ProductBenefitMapping> {
  const normalized: Record<string, ProductBenefitMapping> = {};

  Object.entries(map || {}).forEach(([productId, entry]) => {
    const normalizedProductId = `${productId || ''}`.trim();
    const mapping = normalizePayrollBenefitMapping(entry);
    if (normalizedProductId && mapping) {
      normalized[normalizedProductId] = mapping;
    }
  });

  return normalized;
}
